import { useEffect, useMemo } from 'react';

import { useMultipleFlags } from '@pukka/unleash';
import { FlagNames } from '@pukka/unleash/featureFlagConfig';
import { withPersist } from '@pukka/valtio';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

interface IMarketingModalPersistedState extends Record<string, unknown> {
  open: boolean;
  version: number;
}

interface ModalConfig {
  readonly image: string;
  readonly url?: string;
  readonly cta?: string;
  readonly width?: number;
  readonly height?: number;
  readonly version: number;
  readonly caption?: string;
}

const { proxy: MarketingModalPersistedState } = withPersist<IMarketingModalPersistedState>(
  {
    open: false,
    version: 0,
  },
  'marketing',
  {
    expiry: 6_048_000_000, // a week
    session: 'user',
  },
);

function onCloseModal() {
  MarketingModalPersistedState.open = false;
}

const flags: FlagNames[] = ['marketing-modal-backoffice', 'marketing-modal-backoffice-2', 'marketing-modal-backoffice-3'];

function useImageMarketingModal(useFlags = useMultipleFlags, PersistedState = MarketingModalPersistedState) {
  const { variant: marketingModal } = useFlags(flags);

  const modalConfig: ModalConfig = useMemo(() => {
    return (
      (marketingModal?.enabled &&
        marketingModal.name === 'image' &&
        marketingModal.payload?.type === 'json' &&
        JSON.parse(marketingModal.payload.value)) ||
      {}
    );
  }, [marketingModal]);

  useEffect(() => {
    if (marketingModal?.enabled && modalConfig.version > PersistedState.version) {
      PersistedState.open = true;
      PersistedState.version = modalConfig.version;
    } else if (!marketingModal?.enabled) {
      PersistedState.open = false;
    }
  }, [marketingModal, PersistedState, modalConfig.version]);

  return modalConfig;
}

function getUnsafeHTMLContent(caption: string) {
  if (caption) {
    const markdown = DOMPurify.sanitize(marked.parse(caption) as string);

    return { __html: markdown };
  }

  return undefined;
}

export { MarketingModalPersistedState, onCloseModal, useImageMarketingModal, getUnsafeHTMLContent };
